import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import flags from 'react-phone-number-input/flags';
import { useRegion, Region, timezoneCountryIso } from '../../../../contexts/RegionContext/RegionContext';
import { useThemeMode } from '../../../../theme/ThemeProvider';
import { useRole } from '../../../../contexts/RoleContext/RoleContext';

/* ── India tricolor SVG flag ───────────────────────────────── */
const IndiaFlag = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={Math.round(size * 0.67)}
    viewBox="0 0 30 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}
    aria-label="India flag"
  >
    {/* Saffron */}
    <rect width="30" height="6.67" fill="#FF9933" />
    {/* White */}
    <rect y="6.67" width="30" height="6.67" fill="#FFFFFF" />
    {/* Green */}
    <rect y="13.33" width="30" height="6.67" fill="#138808" />
    {/* Ashoka Chakra — simplified circle with spokes */}
    <circle cx="15" cy="10" r="2.8" fill="none" stroke="#000080" strokeWidth="0.6" />
    {/* 8 main spokes */}
    {Array.from({ length: 8 }).map((_, i) => {
      const angle = (i * Math.PI) / 4;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return (
        <line
          key={i}
          x1={15 + cos * 0.5}
          y1={10 + sin * 0.5}
          x2={15 + cos * 2.2}
          y2={10 + sin * 2.2}
          stroke="#000080"
          strokeWidth="0.45"
        />
      );
    })}
    <circle cx="15" cy="10" r="0.55" fill="#000080" />
  </svg>
);

/* ── Globe SVG (Global) ─────────────────────────────────────── */
const GlobeIcon = ({ size = 16, className }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Global"
  >
    {/* Outer circle */}
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5" />
    {/* Vertical centre meridian */}
    <ellipse cx="12" cy="12" rx="3.6" ry="9.5" stroke="currentColor" strokeWidth="1.2" />
    {/* Equator */}
    <line x1="2.5" y1="12" x2="21.5" y2="12" stroke="currentColor" strokeWidth="1.2" />
    {/* Upper latitude */}
    <path d="M4.5 7.5 Q12 5.5 19.5 7.5" stroke="currentColor" strokeWidth="1.1" fill="none" />
    {/* Lower latitude */}
    <path d="M4.5 16.5 Q12 18.5 19.5 16.5" stroke="currentColor" strokeWidth="1.1" fill="none" />
  </svg>
);

/* ── Country flag (same flag set as the phone field) ────────── */
const CountryFlag = ({ iso, size = 16 }: { iso: string; size?: number }) => {
  const Flag = (flags as Record<string, React.FC<{ title?: string }>>)[iso];
  if (!Flag) return null;
  return (
    <span
      className="inline-flex overflow-hidden rounded-[2px] flex-shrink-0 shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
      style={{ width: size, height: Math.round(size * 0.67) }}
    >
      <Flag title={iso} />
    </span>
  );
};

const countryName = (iso: string): string => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || iso;
  } catch {
    return iso;
  }
};

/* ── Region options ─────────────────────────────────────────── */
type RegionOption = {
  value: Region;
  label: string;
  /** Shown after the label in the list, e.g. "Detected". */
  hint?: string;
  Icon: React.FC<{ size?: number; className?: string }>;
};

const INDIA: RegionOption = {
  value: 'India',
  label: 'India',
  Icon: ({ size = 16 }) => <IndiaFlag size={size} />,
};

/**
 * "Global" is shown as the patient's own country when the timezone gives one (Bulgaria,
 * United States…), otherwise as "International". Only the label changes: the region rules
 * (consent wording, phone codes) stay India vs Global.
 */
function globalOption(): RegionOption {
  const iso = timezoneCountryIso();
  if (iso && iso !== 'IN' && (flags as Record<string, unknown>)[iso]) {
    return {
      value: 'Global',
      label: countryName(iso),
      hint: 'Detected',
      Icon: ({ size = 16 }) => <CountryFlag iso={iso} size={size} />,
    };
  }
  return {
    value: 'Global',
    label: 'International',
    Icon: ({ size = 16, className }) => <GlobeIcon size={size} className={className} />,
  };
}

/* ── Component ──────────────────────────────────────────────── */
export default function RegionSelector() {
  const { role } = useRole();
  const { region, setRegion, isRegionLocked } = useRegion();
  const { isDark } = useThemeMode();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // the patient's own country first, then India
  const options = useMemo(() => [globalOption(), INDIA], []);

  useEffect(() => {
    if (role !== 'patient') return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [role]);

  useEffect(() => {
    if (role !== 'patient') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [role]);

  if (role !== 'patient') {
    return null;
  }

  if (isRegionLocked) {
    return null;
  }

  const current = options.find(r => r.value === region) ?? options[0];
  const CurrentIcon = current.Icon;
  const isGlobe = (opt: RegionOption) => opt.value === 'Global' && !opt.hint;

  return (
    <div className="relative" ref={ref}>

      {/* ── Compact trigger ── */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Region: ${current.label}`}
        title={`Region: ${current.label}`}
        className={[
          'flex items-center gap-1.5 h-8 pl-2 pr-1 rounded-lg select-none outline-none',
          'transition-all duration-150',
          open
            ? isDark
              ? 'bg-white/[0.09] ring-1 ring-white/[0.12]'
              : 'bg-slate-100 ring-1 ring-slate-200'
            : isDark
              ? 'hover:bg-white/[0.06]'
              : 'hover:bg-slate-100/80',
        ].join(' ')}
      >
        {/* Region icon */}
        <span className="flex items-center justify-center">
          <CurrentIcon
            size={isGlobe(current) ? 15 : 18}
            className={
              isGlobe(current)
                ? isDark ? 'text-slate-300' : 'text-slate-500'
                : undefined
            }
          />
        </span>

        {/* Country name (the flag alone on small screens) */}
        <span className={[
          'hidden sm:inline max-w-[120px] truncate text-[12.5px] font-medium',
          isDark ? 'text-slate-200' : 'text-slate-600',
        ].join(' ')}>
          {current.label}
        </span>

        {/* Animated chevron */}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className={['hidden sm:inline', isDark ? 'text-white/30' : 'text-slate-400'].join(' ')}
        >
          <ChevronDown size={11} strokeWidth={2.5} />
        </motion.span>
      </button>

      {/* ── Slim dropdown ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -5, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
            role="listbox"
            aria-label="Select region"
            className={[
              'absolute right-0 top-[calc(100%+6px)] z-[99999]',
              'w-max min-w-[200px] max-w-[280px] rounded-xl overflow-hidden py-1',
              'shadow-[0_8px_30px_-4px_rgba(0,0,0,0.18),0_2px_8px_-2px_rgba(0,0,0,0.10)]',
              isDark
                ? 'bg-[#131E30] border border-white/[0.08]'
                : 'bg-white border border-slate-200/70',
            ].join(' ')}
          >
            {/* Section label */}
            <p className={[
              'px-3 pt-1.5 pb-1 text-[9.5px] font-bold uppercase tracking-[0.08em]',
              isDark ? 'text-white/30' : 'text-slate-400',
            ].join(' ')}>
              Your region
            </p>

            {options.map(opt => {
              const isActive = opt.value === region;
              const OptionIcon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { setRegion(opt.value); setOpen(false); }}
                  className={[
                    'w-full flex items-center gap-2.5 px-3 py-[7px] text-left transition-colors duration-100',
                    isActive
                      ? isDark
                        ? 'bg-brand-500/[0.14] text-white'
                        : 'bg-brand-50 text-brand-700'
                      : isDark
                        ? 'text-slate-300 hover:bg-white/[0.05] hover:text-white'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800',
                  ].join(' ')}
                >
                  {/* Icon */}
                  <span className="flex items-center justify-center w-4 flex-shrink-0">
                    <OptionIcon
                      size={isGlobe(opt) ? 14 : 16}
                      className={
                        isGlobe(opt)
                          ? isActive
                            ? isDark ? 'text-brand-300' : 'text-brand-600'
                            : isDark ? 'text-slate-300' : 'text-slate-500'
                          : undefined
                      }
                    />
                  </span>

                  {/* Label */}
                  <span className={[
                    'flex-1 text-[12.5px] font-medium whitespace-nowrap truncate',
                    isActive
                      ? isDark ? 'text-white' : 'text-brand-700'
                      : isDark ? 'text-slate-200' : 'text-slate-700',
                  ].join(' ')}>
                    {opt.label}
                    {opt.hint && (
                      <span className={[
                        'ml-1.5 text-[10.5px] font-semibold',
                        isDark ? 'text-white/40' : 'text-slate-400',
                      ].join(' ')}>
                        {opt.hint}
                      </span>
                    )}
                  </span>

                  {/* Active checkmark */}
                  {isActive && (
                    <Check
                      size={11}
                      strokeWidth={2.8}
                      className={isDark ? 'text-brand-400 flex-shrink-0' : 'text-brand-500 flex-shrink-0'}
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
